import { AllowedFlags } from 'hooks/useFeatures';
import { UserType } from 'models/Users';
import type { DynamicNavigationItems, NavigationProps } from './Header.types';

export const agencyNavigationItems = ({
  allowedFlags,
  dynamicItems,
}: {
  allowedFlags: AllowedFlags;
  dynamicItems?: DynamicNavigationItems;
}): NavigationProps => ({
  navItems: [
    {
      path: '/dashboard',
      labelKey: 'common.dashboard',
      unreadType: 'lead',
    },
    {
      path: '/inbox',
      labelKey: 'common.inbox',
      unreadType: 'message',
    },
    {
      path: '/pipeline',
      labelKey: 'common.pipeline',
    },
    {
      path: '/calendar',
      labelKey: 'common.calendar',
    },
    {
      path: '/analytics',
      labelKey: 'common.analytics',
    },
  ],
  dropdownItems: [
    {
      path: '/properties',
      labelKey: 'common.properties',
    },
    {
      path: '/publishing',
      labelKey: 'common.publishingTools',
    },
    {
      path: '/third-party-agency',
      labelKey: 'common.thirdPartyAgency',
    },
    ...(allowedFlags.allowAutomationV3
      ? []
      : [
          {
            path: '/calendar-import',
            labelKey: 'common.calendarTools',
          },
        ]),
    {
      path: allowedFlags.allowIntegrationsV3
        ? '/integrations'
        : '/integrations-migrated',
      labelKey: 'common.integrations',
    },
    {
      path: allowedFlags.allowChannelsV3 ? '/channels' : '/channels-migrated',
      labelKey: 'common.channels',
    },
    {
      path: '/reports',
      labelKey: 'common.reports',
    },
    {
      path: allowedFlags.allowAutomationV3
        ? '/automations-migrated'
        : '/my-templates',
      labelKey: allowedFlags.allowAutomationV3
        ? 'common.automation'
        : 'common.templatesAndTriggers',
    },
    {
      path: '/direct-booking-engine',
      labelKey: 'common.directBookingEngine',
      allowedToRender: ({ allowDbsSettingsV3 }) => allowDbsSettingsV3,
    },
    {
      path: '/owners',
      labelKey: 'common.owners',
    },
    {
      path: '/promo-codes',
      labelKey: 'common.promoCodes',
    },
    {
      path: '/agency-settings',
      labelKey: 'common.agencySettings',
    },
  ],
  dropdownSecondaryItems: [
    ...(dynamicItems?.assist?.isVisible
      ? [
          {
            path: dynamicItems.assist.path,
            labelKey: 'common.assist',
            badge: {
              labelKey: 'common.new',
            },
          },
        ]
      : []),
    {
      path: '/screen-and-protect',
      labelKey: 'common.screenAndProtect',
      badge: {
        labelKey: 'common.new',
      },
    },
    {
      path: '/refer',
      labelKey: 'common.referFriend',
    },
    {
      path: '/logout',
      labelKey: 'common.signOut',
    },
  ],
});

export const agentNavigationItems: NavigationProps = {
  navItems: [
    {
      path: '/guest/dashboard',
      labelKey: 'common.myBookings',
    },
    {
      path: '/guest/account',
      labelKey: 'common.accountAndPreferences',
    },
  ],
  dropdownItems: [
    {
      path: '/logout',
      labelKey: 'common.signOut',
    },
  ],
};

export const serviceProviderNavigationItems: NavigationProps = {
  navItems: [
    {
      path: '/service-provider/dashboard',
      labelKey: 'common.jobs',
    },
    {
      path: '/service-provider/properties',
      labelKey: 'common.properties',
    },
    {
      path: '/service-provider/account',
      labelKey: 'common.myAccount',
    },
  ],
  dropdownItems: [
    {
      path: '/logout',
      labelKey: 'common.signOut',
    },
  ],
};

export const ownerNavigationItems: NavigationProps = {
  navItems: [
    {
      path: '/owner/analytics',
      labelKey: 'common.analytics',
    },
    {
      path: '/owner/calendar',
      labelKey: 'common.calendar',
    },
    {
      path: '/owner/inbox',
      labelKey: 'common.inbox',
      unreadType: 'message',
    },
    {
      path: '/owner/properties',
      labelKey: 'common.properties',
    },
    {
      path: '/owner/statements',
      labelKey: 'common.statements',
    },
  ],
  dropdownItems: [
    {
      path: '/owner/account',
      labelKey: 'common.settings',
    },
    {
      path: '/logout',
      labelKey: 'common.signOut',
    },
  ],
};

export const itemsByRole = ({
  allowedFlags,
  dynamicItems,
}: {
  allowedFlags: AllowedFlags;
  dynamicItems?: DynamicNavigationItems;
}): Record<UserType, NavigationProps> => ({
  [UserType.Employee]: agencyNavigationItems({ allowedFlags, dynamicItems }),
  [UserType.Owner]: ownerNavigationItems,
  [UserType.ServiceProviderUser]: serviceProviderNavigationItems,
  [UserType.Agent]: agentNavigationItems,
});
